import { memo } from 'react'
import {
    MarkdownTextPrimitive,
    unstable_memoizeMarkdownComponents as memoizeMarkdownComponents,
    useIsMarkdownCodeBlock,
} from '@assistant-ui/react-markdown'
import '@assistant-ui/react-markdown/styles/dot.css'
import remarkGfm from 'remark-gfm'
import { Table } from '@/components/ui/table'
import {
    Blockquote,
    InlineCode,
    Large,
    List,
    Medium,
    Paragraph,
    Small,
} from '@/components/ui/typography'
import { cn } from '@/lib/utils'

const markdownComponents = memoizeMarkdownComponents({
    h1: ({ className, ...props }) => (
        <Large
            className={cn('mb-2 mt-3 first:mt-0 last:mb-0', className)}
            {...props}
        />
    ),
    h2: ({ className, ...props }) => (
        <Medium
            className={cn(
                'mb-2 mt-3 font-semibold first:mt-0 last:mb-0',
                className,
            )}
            {...props}
        />
    ),
    h3: ({ className, ...props }) => (
        <Small
            className={cn(
                'mb-1.5 mt-2.5 font-semibold first:mt-0 last:mb-0',
                className,
            )}
            {...props}
        />
    ),
    p: ({ className, ...props }) => (
        <Paragraph
            noDefaultMargin
            className={cn(
                'my-2 leading-relaxed first:mt-0 last:mb-0',
                className,
            )}
            {...props}
        />
    ),
    a: ({ className, ...props }) => (
        <a
            className={cn('underline underline-offset-2', className)}
            target="_blank"
            rel="noreferrer"
            {...props}
        />
    ),
    ul: ({ className, ...props }) => (
        <List
            className={cn('my-2 ml-5 first:mt-0 last:mb-0', className)}
            {...props}
        />
    ),
    ol: ({ className, ...props }) => (
        <ol
            className={cn(
                'my-2 ml-5 list-decimal first:mt-0 last:mb-0',
                className,
            )}
            {...props}
        />
    ),
    li: ({ className, ...props }) => (
        <li className={cn('my-0.5', className)} {...props} />
    ),
    strong: ({ className, ...props }) => (
        <strong className={cn('font-semibold', className)} {...props} />
    ),
    blockquote: ({ className, ...props }) => (
        <Blockquote
            className={cn('my-2 pl-3 first:mt-0 last:mb-0', className)}
            {...props}
        />
    ),
    hr: ({ className, ...props }) => (
        <hr className={cn('my-3 border-border', className)} {...props} />
    ),
    table: ({ className, ...props }) => (
        <Table
            className={cn(
                'my-2 border-collapse text-left first:mt-0 last:mb-0',
                className,
            )}
            {...props}
        />
    ),
    th: ({ className, ...props }) => (
        <th
            className={cn(
                'border-b border-border px-2 py-1 font-semibold',
                className,
            )}
            {...props}
        />
    ),
    td: ({ className, ...props }) => (
        <td
            className={cn('border-b border-border px-2 py-1', className)}
            {...props}
        />
    ),
    pre: ({ className, ...props }) => (
        <pre
            className={cn(
                'my-2 overflow-x-auto rounded-md bg-muted p-3 font-mono text-[0.85em] first:mt-0 last:mb-0',
                className,
            )}
            {...props}
        />
    ),
    code: function Code({ className, ...props }) {
        const isCodeBlock = useIsMarkdownCodeBlock()
        return (
            <InlineCode
                className={cn(
                    isCodeBlock && 'bg-transparent p-0 font-normal',
                    className,
                )}
                {...props}
            />
        )
    },
})

export const MarkdownText = memo(function MarkdownText() {
    return (
        <MarkdownTextPrimitive
            remarkPlugins={[remarkGfm]}
            className="aui-md"
            components={markdownComponents}
        />
    )
})
